import React, { useState } from 'react';
import BuyerProfileInfoDialog from '../BuyerProfileInfoDialog';
import { getResidentialUnitBuyerInfo } from '@/services/residential-units';
import { useAsyncState, useActiveProjectBuyer } from '@/utils/hooks';
import theme from './ResidentialUnitBuyerInfo.module.scss';
import { Trans } from '@lingui/react/macro';
import { UserIcon, StackedIcons } from '@/components/Icons';
import { DateDistanceDisplay } from '@/components/localization';
import ResidentialUnitInviteBuyerDialog from '../ResidentialUnitInviteBuyerDialog';
import { useSelector } from 'react-redux';
import { IState } from '@/state/IState';
import { faUserPlus, faUserCheck } from '@fortawesome/pro-light-svg-icons';
import { ProjectPermissionToggle } from '@/components/permissions';
import { ResidentialUnit } from '@/types';

interface ResidentialUnitBuyerInfoProps {
    residentialUnit: ResidentialUnit;
}

const ResidentialUnitBuyerInfo: React.FC<ResidentialUnitBuyerInfoProps> = ({ residentialUnit }) => {
    const project = useSelector((state: IState) => state.global.activeProject!);
    const residentialUnitId = residentialUnit.id;

    const [showBuyerInfoDialog, setShowBuyerInfoDialog] = useState(false);
    const [showInviteBuyerDialog, setShowInviteBuyerDialog] = useState(false);
    const [buyerInfo, , reloadBuyerInfo] = useAsyncState(
        () => getResidentialUnitBuyerInfo(residentialUnitId),
        [residentialUnitId],
        null
    );

    const activeProjectUsers = useActiveProjectBuyer();
    const isOnline = () => {
        return activeProjectUsers.filter((x) => buyerInfo?.users.map((x) => x.userId).includes(x.id)).length > 0;
    };
    const handleBuyerClick = () => {
        if (!buyerInfo) {
            return;
        }

        if (buyerInfo.users.length > 0) {
            setShowBuyerInfoDialog(true);
        } else {
            setShowInviteBuyerDialog(true);
        }
    };

    const handleInfoModalClose = (needsReload: boolean) => {
        if (needsReload) {
            reloadBuyerInfo();
        }

        setShowBuyerInfoDialog(false);
    };

    const getBuyerNames = () => {
        const users = buyerInfo!.users;
        if (users.length === 0) {
            return <Trans>No buyer</Trans>;
        }

        if (users.length === 1) {
            const user = users[0];

            return (
                `${user.salutation ? `${user.salutation} ` : ''}${user.title ? `${user.title} ` : ''}${user.firstName || ''} ${user.lastName || ''}`.trim() ||
                user.email ||
                user.phoneNumber
            );
        }

        return users.map((x) => x.firstName).join(', ');
    };

    function renderAvatarsIcons() {
        if (!buyerInfo || buyerInfo.users.length === 0) {
            return <UserIcon sizeInPx={38} className={theme.avatar} userId={0} emptyIcon={faUserPlus} />;
        }

        return (
            <StackedIcons>
                {buyerInfo.users.map((x) => (
                    <UserIcon
                        emptyIcon={x.invitedDate && !x.acceptedDate ? faUserCheck : undefined}
                        key={x.inviteId}
                        sizeInPx={36}
                        userId={x.userId || 0}
                    />
                ))}
            </StackedIcons>
        );
    }

    function renderContent() {
        if (!buyerInfo) {
            return null;
        }

        return (
            <>
                <BuyerProfileInfoDialog
                    residentialUnitId={residentialUnitId}
                    buyerInfo={buyerInfo}
                    show={showBuyerInfoDialog}
                    onClose={handleInfoModalClose}
                    onBuyerAdded={() => reloadBuyerInfo()}
                />

                <ResidentialUnitInviteBuyerDialog
                    onClose={() => setShowInviteBuyerDialog(!showInviteBuyerDialog)}
                    onAccept={() => reloadBuyerInfo()}
                    show={showInviteBuyerDialog}
                    residentialUnitId={residentialUnitId}
                />

                {renderAvatarsIcons()}

                <div className={theme.text}>
                    <div className={theme.title}>
                        {buyerInfo.users.length === 1 ? <Trans>Buyer</Trans> : <Trans>Buyers</Trans>}
                    </div>

                    <div className={theme.name}>{getBuyerNames()}</div>
                    {isOnline() ? (
                        <small>
                            <Trans>Active now</Trans>
                        </small>
                    ) : (
                        buyerInfo.lastVisitDate && (
                            <small>
                                <DateDistanceDisplay
                                    placement="bottom"
                                    prefix={
                                        <>
                                            <Trans>Last login:</Trans>&nbsp;
                                        </>
                                    }
                                    value={buyerInfo.lastVisitDate}
                                />
                            </small>
                        )
                    )}

                    {buyerInfo.users.length === 0 && (
                        <ProjectPermissionToggle condition="canInviteBuyers">
                            <span className={theme.invitationLink}>
                                <Trans>Invite buyer</Trans>
                            </span>
                        </ProjectPermissionToggle>
                    )}
                </div>
            </>
        );
    }
    if (!buyerInfo) {
        return <div className={theme.buyerBox}></div>;
    }

    return (
        <>
            {project.permissions.canInviteBuyers || buyerInfo.users.length > 0 ? (
                <button className={theme.buyerBox} onClick={handleBuyerClick}>
                    {renderContent()}
                </button>
            ) : (
                <div className={theme.buyerBox}>{renderContent()}</div>
            )}
        </>
    );
};

export default ResidentialUnitBuyerInfo;
